/**
 * ReportExportBar — the "Download this report" strip (Markdown / PDF / Word).
 *
 * Extracted from ResultColumn so BOTH the active answer column AND every
 * PAST turn (rendered in ChatPanel) can offer working client-side export.
 * Before this, once a run finished and the user sent the next message, the
 * answer moved from ResultColumn (which owned the export strip) into
 * ChatPanel's past-turn renderer — which had NO export UI — so the download
 * buttons for that report disappeared.
 *
 * It owns its own export handler, mirroring ResultColumn's fixed `runExport`:
 *   - opens the PDF print window SYNCHRONOUSLY (before any await) so the
 *     click's user-activation stays live (otherwise window.print() emits only
 *     the first page),
 *   - collects rendered mermaid diagrams scoped to its OWN report root
 *     ([data-ma-report-root] ancestor) so it rasterizes THIS turn's diagrams,
 *     not another turn's,
 *   - assembles the export body with buildExportMarkdown, which keeps every
 *     citation's label inline (as before) and appends "Appendix — cited evidence"
 *     carrying the cited artifacts' ACTUAL rows/code — the exported file used to
 *     name its evidence and contain none of it,
 *   - delegates to downloadMarkdownAs with { diagrams, title, printWin }.
 *
 * The appendix has to FETCH the cited artifacts, so export is now genuinely async:
 * the clicked format's button spins and the others disable until the file is built.
 */
import { useRef, useState } from "react";
import { Download } from "lucide-react";
import toast from "react-hot-toast";
import { exportArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import { buildExportMarkdown } from "@/services/multi-agent/exportAppendix";
import {
  downloadMarkdownAs,
  collectRenderedDiagrams,
  writePrintPlaceholder,
  type ReportDiagram,
} from "@/services/multi-agent/exportArtifact";
import { Button } from "@/components/atoms/multi-agent/Button";

interface ReportExportBarProps {
  /** The report markdown to export (RAW — keep the `artifact://` citations in it:
   *  they are stripped for the body here AND read to build the cited-evidence
   *  appendix. A caller that pre-strips them exports a report with no appendix). */
  markdown: string;
  /** Optional title used as the report heading / PDF window title / filename hint. */
  title?: string;
}

export function ReportExportBar({ markdown, title }: ReportExportBarProps) {
  // Anchor used to scope mermaid-diagram collection to THIS report's DOM
  // subtree. We walk up to the nearest [data-ma-report-root] container so a
  // past turn's export never rasterizes another turn's diagrams.
  const rootRef = useRef<HTMLDivElement>(null);
  // Which format is currently being assembled. The appendix has to FETCH the cited
  // artifacts, so a click is no longer instant on a heavily-cited report; without
  // this the user gets no feedback and clicks again, starting a second export.
  const [busyFormat, setBusyFormat] = useState<"md" | "pdf" | "docx" | null>(null);

  // Render-time emptiness check only — the real export body is assembled in
  // runExport (async, because it inlines the cited artifacts' data).
  const exportMd = exportArtifactMarkdown(markdown || "");
  if (!exportMd) return null;

  const runExport = async (ext: "md" | "pdf" | "docx") => {
    if (!exportMd || busyFormat) return;
    setBusyFormat(ext);
    // Open the print window SYNCHRONOUSLY (before any await) so the click's
    // user-activation is still live — otherwise the browser backgrounds the
    // popup and window.print() renders only the first page.
    const printWin = ext === "pdf" ? window.open("", "_blank") : null;
    // Give the popup something to be while the appendix is fetched — a blank tab for
    // several seconds looks broken and can be reclaimed by the browser.
    writePrintPlaceholder(printWin, title || "Multi-agent report");
    let diagrams: ReportDiagram[] = [];
    if (ext !== "md") {
      // Scope the diagram search to THIS report's root so we don't pick up
      // another turn's (or the active column's) mermaid SVGs.
      const root =
        (rootRef.current?.closest("[data-ma-report-root]") as HTMLElement | null) ||
        rootRef.current;
      try {
        diagrams = await collectRenderedDiagrams(root);
      } catch {
        diagrams = [];
      }
    }
    try {
      // Body + "Appendix — cited evidence": every `artifact://` citation in the
      // report contributes its actual rows/code to the end of the file, so the
      // exported document can be read (and checked) away from the app.
      const body = await buildExportMarkdown(markdown || "");
      await downloadMarkdownAs(body, ext, "multi-agent-report", {
        diagrams,
        title: (title || "Multi-agent report").slice(0, 90),
        printWin,
      });
    } catch (e) {
      // Close a dangling blank print window if the export threw before it wrote.
      try { printWin?.close(); } catch { /* ignore */ }
      toast.error(`Export failed: ${(e as Error)?.message ?? String(e)}`);
    } finally {
      setBusyFormat(null);
    }
  };

  return (
    <div
      ref={rootRef}
      className="ma-fade-up mt-6 flex flex-wrap items-center gap-2 pt-4"
      style={{ borderTop: "1px solid var(--ma-border)" }}
    >
      <span className="ma-micro" style={{ color: "var(--ma-text-muted)" }}>
        Download this report
      </span>
      {/* `busy` on the clicked format spins ITS icon; the others are disabled so a
          second export can't race the first (they share the print window and the
          artifact fetches). */}
      <Button
        variant="ghost"
        size="sm"
        busy={busyFormat === "md"}
        disabled={busyFormat !== null}
        leadingIcon={<Download className="h-3.5 w-3.5" />}
        onClick={() => void runExport("md")}
      >
        Markdown
      </Button>
      <Button
        variant="ghost"
        size="sm"
        busy={busyFormat === "pdf"}
        disabled={busyFormat !== null}
        leadingIcon={<Download className="h-3.5 w-3.5" />}
        onClick={() => void runExport("pdf")}
      >
        PDF
      </Button>
      <Button
        variant="ghost"
        size="sm"
        busy={busyFormat === "docx"}
        disabled={busyFormat !== null}
        leadingIcon={<Download className="h-3.5 w-3.5" />}
        onClick={() => void runExport("docx")}
      >
        Word
      </Button>
    </div>
  );
}
